/**
 * The alert text keeps the Administrator's color whenever it is readable, and
 * otherwise the nearest readable shade of it, so a pale yellow chosen in light
 * mode or a deep navy in dark mode still meets WCAG AA (4.5:1) on the banner.
 */

export type AlertTheme = 'light' | 'dark'

/** The banner surface of each theme: `--color-surface-raised` in tokens.css. */
export const ALERT_SURFACES: Record<AlertTheme, string> = {
  light: '#fffefa',
  dark: '#192527',
}

export const MINIMUM_CONTRAST = 4.5

type Rgb = [number, number, number]

function parse(hex: string): Rgb {
  return [1, 3, 5].map((index) =>
    parseInt(hex.slice(index, index + 2), 16)
  ) as Rgb
}

function format([red, green, blue]: Rgb) {
  return `#${[red, green, blue]
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')}`
}

function luminance(rgb: Rgb) {
  const [red, green, blue] = rgb.map((channel) => {
    const value = channel / 255
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }) as Rgb
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

export function contrastRatio(first: string, second: string) {
  const [lighter, darker] = [
    luminance(parse(first)),
    luminance(parse(second)),
  ].sort((a, b) => b - a)
  return (lighter! + 0.05) / (darker! + 0.05)
}

/**
 * The chosen color if it reads on the theme's surface, else the first mix of
 * it toward black (light theme) or white (dark theme) that does.
 */
export function readableAlertColor(color: string, theme: AlertTheme) {
  const surface = ALERT_SURFACES[theme]
  if (contrastRatio(color, surface) >= MINIMUM_CONTRAST) return color
  const base = parse(color)
  const target: Rgb = theme === 'light' ? [0, 0, 0] : [255, 255, 255]
  for (let step = 1; step <= 20; step += 1) {
    const weight = step / 20
    const mixed = format(
      base.map(
        (channel, index) => channel + (target[index]! - channel) * weight
      ) as Rgb
    )
    if (contrastRatio(mixed, surface) >= MINIMUM_CONTRAST) return mixed
  }
  return format(target)
}
